/**
 * WebSocket client for the realtime server, with a persistent player id and
 * automatic reconnect (which the server treats as a resync, not a new session).
 */

import type { ClientMsg, ServerMsg } from "@xq/protocol";
import { getToken } from "./account.js";
import { event } from "./analytics.js";

function persistentId(): string {
  const KEY = "xq-player-id";
  let id = localStorage.getItem(KEY);
  if (!id || id.length < 8) {
    id = "P" + Math.random().toString(36).slice(2) + Math.random().toString(36).slice(2);
    localStorage.setItem(KEY, id);
  }
  return id;
}

export class Net {
  readonly playerId = persistentId();
  private ws: WebSocket | null = null;
  private url: string;
  private name: string;
  private handlers: ((m: ServerMsg) => void)[] = [];
  private openHandlers: (() => void)[] = [];
  private statusHandlers: ((connected: boolean) => void)[] = [];
  private queueOut: ClientMsg[] = [];
  private retry = 0;
  connected = false;

  constructor(name: string) {
    this.name = name;
    // Same host as the page, path /ws. Works over http (ws) and https (wss).
    const proto = location.protocol === "https:" ? "wss" : "ws";
    this.url = `${proto}://${location.host}/ws`;
  }

  setName(name: string) {
    this.name = name;
  }

  connect() {
    if (this.ws && (this.ws.readyState === WebSocket.OPEN || this.ws.readyState === WebSocket.CONNECTING)) return;
    let ws: WebSocket;
    try {
      ws = new WebSocket(this.url);
    } catch {
      // The constructor itself can throw, before any network is involved: a
      // browser or extension that replaces WebSocket, or a page served through
      // something that rewrites its address. This is called from inside a
      // render, so letting it escape took the whole Play screen down with it
      // for a visitor who could otherwise have read "Reconnecting…".
      this.constructFailed();
      return;
    }
    this.ws = ws;
    ws.onopen = () => {
      this.connected = true;
      this.retry = 0;
      // A token (if signed in) makes the server bind this socket to the account.
      const token = getToken();
      this.rawSend({ t: "hello", playerId: this.playerId, name: this.name, ...(token ? { token } : {}) });
      for (const m of this.queueOut) this.rawSend(m);
      this.queueOut = [];
      this.openHandlers.forEach((h) => h());
      this.statusHandlers.forEach((h) => h(true));
    };
    ws.onmessage = (ev) => {
      let msg: ServerMsg;
      try {
        msg = JSON.parse(ev.data);
      } catch {
        return;
      }
      this.handlers.forEach((h) => h(msg));
    };
    ws.onclose = () => {
      this.connected = false;
      this.ws = null;
      this.statusHandlers.forEach((h) => h(false));
      this.retry = Math.min(this.retry + 1, 6);
      setTimeout(() => this.connect(), 400 * this.retry);
    };
    ws.onerror = () => ws.close();
  }

  /** The socket could not even be built. Behave as a dropped connection does,
   *  but give up after a few spaced attempts: a dropped connection comes back,
   *  whereas a constructor that throws will throw the same way every time. */
  private constructFailed() {
    this.connected = false;
    this.ws = null;
    this.statusHandlers.forEach((h) => h(false));
    // Counted once per page, as an event rather than an error: it is the
    // visitor's environment, not a fault anyone here can act on.
    if (this.retry === 0) event("ws_blocked", "construct");
    if (this.retry >= 6) return;
    this.retry += 1;
    setTimeout(() => this.connect(), 400 * this.retry);
  }

  /** Drop the current socket and reconnect, so the next hello re-sends identity
   *  (used after login/logout so the server rebinds the connection to the account). */
  reconnectFresh() {
    const old = this.ws;
    if (old) {
      old.onclose = null;
      old.onmessage = null;
      old.onerror = null;
      old.onopen = null;
      try { old.close(); } catch { /* ignore */ }
    }
    this.ws = null;
    this.connected = false;
    this.retry = 0;
    this.connect();
  }

  private rawSend(m: ClientMsg) {
    this.ws?.send(JSON.stringify(m));
  }

  send(m: ClientMsg) {
    if (this.connected) this.rawSend(m);
    else this.queueOut.push(m);
  }

  onMessage(h: (m: ServerMsg) => void) {
    this.handlers.push(h);
  }
  onOpen(h: () => void) {
    this.openHandlers.push(h);
  }
  onStatus(h: (connected: boolean) => void) {
    this.statusHandlers.push(h);
  }
}
